<script setup lang="ts">
import { computed, ref } from 'vue'
import Avatar from '../Avatar/Avatar.vue'
import type { MessageData } from './types'
import TypingMessage from '@/components/TypingMessage.vue'
import StreamingMessage from '@/components/StreamingMessage.vue'
import { cn } from '@/lib/utils'
import { formatAMPM } from '@/helpers'
import { useStreamingMarkdown } from '@/composables/useStreamingMarkdown'

const props = defineProps<MessageData>()
const isHovered = ref(false)

function onMouseEnter() {
  isHovered.value = true
}

function onMouseLeave() {
  isHovered.value = false
}

const messageTime = props.time ? formatAMPM(props.time instanceof Date ? props.time : new Date(props.time)) : null

// Use streaming markdown parser for better rendering
const { parseStreamingMarkdown } = useStreamingMarkdown()

const messageContent = computed(() => {
  return typeof props.messageContent === 'string'
    ? props.messageContent
    : props.messageContent?.text ?? ''
})

const isStreamingCapable = computed(() => {
  // Use streaming if message is marked as streaming or contains markdown
  if (props.isStreamingMessage) {
    return true
  }

  const content = messageContent.value
  return content.includes('|') || content.includes('```') || content.includes('**') || content.includes('*')
})

const parsedContent = computed(() => {
  return parseStreamingMarkdown(messageContent.value)
})
</script>

<template>
  <div
    :class="cn('conversation-chat pt-[4px] pb-[4px]', props.isAfterMessage ? 'pt-[8px]' : '', props.isBeforeMessage ? 'pb-[8px]' : '', $attrs.class ?? '')"
    tabindex="-1">
    <div class="chat-wrapper">
      <Avatar v-if="!props.isReply || props.isLoadingMessage" :is-chat="true" :image-src="messageAvatar" />
      <div
        :class="cn('netwatch-comment rounded-lg', isReply ? 'float-right font-semibold text-white bg-[#2B7B9D]  rounded-se-0' : 'text-black bg-[#ebebeb] rounded-es-0')"
        @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
        <div :class="cn(isHovered ? 'opacity-100' : 'opacity-0')"
          class="bg-gray-400:90 text-white text-xs rounded-sm p-1 absolute left-[-20px] top-[-2px] transition-all duration-300">
          {{ messageTime }}
        </div>
        <div tabindex="-1">
          <div class="chat-content">
            <TypingMessage v-if="isLoadingMessage" />
            <template v-else>
              <div v-if="typeof props.messageContent !== 'string' && props.messageContent?.image" class="mb-2">
                <img :src="props.messageContent.image as string" class="rounded-sm">
              </div>
              <!-- Use streaming message component for enhanced markdown rendering -->
              <StreamingMessage v-if="isStreamingCapable || props.isStreamingMessage" :content="messageContent"
                :is-streaming="!!props.isStreamingMessage" :turbo-mode="true" />
              <!-- Fallback to simple HTML for basic messages -->
              <article v-else class="prose m-0 p-0">
                <div v-html="parsedContent" />
              </article>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div />
  </div>
</template>
